import { memo, useCallback, useState } from 'react'

import { HIERARCHY_TYPE_WITH_READONLY_LEGAL_INFO } from 'clickNgo/entities/partner/configs'
import { PartnerAudit } from 'clickNgo/entities/partner/ui/partnerAudit'
import { PartnerLegalInfo } from 'clickNgo/entities/partner/ui/partnerLegalInfo'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'

import { PARTNER_EDITOR_FORM_KEYS, PartnerEditorFormKeys } from '../../../configs'
import { useReadOnlyMenuItem } from '../../../hooks/useReadOnlyMenuItem'
import { partnerEditorTranslation } from '../../../translation'
import { PartnerFormEditableContainer } from '../../partnerFormEditableContainer/PartnerFormEditableContainer'

import {
  useEditPartnerLegalInfoHandler,
  useLegalInfoMandatoryAttachments,
  usePartnerLegalInfoValidation,
} from './hooks'
import { IWrappedPartnerLegalInfoProps } from './models'

import styles from './WrappedPartnerLegalInfo.module.scss'

export const WrappedPartnerLegalInfo = memo<IWrappedPartnerLegalInfoProps>(({ partner }) => {
  const [isAuditVisible, setIsAuditVisible] = useState(false)

  const isDisabled = HIERARCHY_TYPE_WITH_READONLY_LEGAL_INFO.includes(partner.hierarchyType)

  const { isCurrentMenuItemReadOnly } = useReadOnlyMenuItem()
  const hasEntryId = Boolean(partner.entryId)

  const { isAttachmentRequired } = useLegalInfoMandatoryAttachments({
    hasActiveAgreement: partner.hasActiveAgreement,
  })

  const { legalValidationProps } = usePartnerLegalInfoValidation({
    formKeys: PARTNER_EDITOR_FORM_KEYS,
  })

  const { handleOnPartnerLegalChangesApply, handleOnPartnerLegalChangesCancel, errorMessages } =
    useEditPartnerLegalInfoHandler({ partner })

  const handleOnAuditOpenClick = useCallback(() => {
    setIsAuditVisible(true)
  }, [])

  const handleOnAuditCloseClick = useCallback(() => {
    setIsAuditVisible(false)
  }, [])

  return (
    <PartnerFormEditableContainer
      isDisabled={isDisabled}
      partner={partner}
      formKey={PartnerEditorFormKeys.LegalInfo}
      title={PartnerEditorFormKeys.LegalInfo}
      onApply={handleOnPartnerLegalChangesApply}
      onCancel={handleOnPartnerLegalChangesCancel}
      isAttachmentRequired={isAttachmentRequired}
      errorMessages={errorMessages}
      isChangesNeedToBeApproved
    >
      <div className={styles.container}>
        <div className={styles.content}>
          <PartnerLegalInfo
            {...partner}
            {...legalValidationProps}
            isReadOnly={isCurrentMenuItemReadOnly}
            hasEntryId={hasEntryId}
            formKeys={PARTNER_EDITOR_FORM_KEYS}
          />
        </div>
        <div className={styles.footer}>
          <Button
            variant="text"
            className={styles.auditBtn}
            icon="kbase kbase-info-alt"
            onClick={handleOnAuditOpenClick}
          >
            <span className={styles.auditBtnText}>
              <Translation text={partnerEditorTranslation.container.button.audit} span />
            </span>
          </Button>
        </div>
      </div>
      <PartnerAudit
        isVisible={isAuditVisible}
        partnerId={partner.partnerId}
        onClose={handleOnAuditCloseClick}
      />
    </PartnerFormEditableContainer>
  )
})
